import React from 'react';
import clsx from 'clsx';
import { catalogDistributorServices, DistributorID } from 'fym-shared/src/smartlinks/catalog';
import { getDistributorLogo } from 'fym-shared/src/smartlinks/distributorLogos';
import { TypedMemo } from '../../../common/types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole early-access flow; each part uses its own slice of it */
import styles from './EarlyAccess.module.scss';
/* eslint-enable css-modules/no-unused-class */
import { CatalogServiceIcon } from './CatalogServiceIcon';
import { distributorLogoSize } from './distributorLogoSize';
import { distributorLogoUrls } from './distributorLogoUrls';

/** Two letters per distributor. Brand initials, so they are not translated. */
const distributorInitials: Record<string, string> = {
  [DistributorID.DistroKid]: 'DK',
  [DistributorID.TuneCore]: 'TC',
  [DistributorID.CDBaby]: 'CB',
  [DistributorID.Ditto]: 'DM',
  [DistributorID.UnitedMasters]: 'UM',
  [DistributorID.ONErpm]: 'ON',
  [DistributorID.TuneCoreJapan]: 'TJ',
};

const tileClasses = [styles.tile0, styles.tile1, styles.tile2, styles.tile3, styles.tile4];

interface EarlyAccessServiceMarkProps {
  integrationType: string;
  /** 44 px in the modal head, 32 px on a card. */
  isLarge?: boolean;
}

/**
 * The mark of a card or a modal: the music-service icon from fym-shared, the
 * distributor's official logo on its brand plate, or a letter tile for a
 * distributor that has no logo file.
 *
 * design: connector-waitlist a (Connection Deck), `.sm` (32 px, 9 px radius)
 * and `.sm.lg` (44 px, 12 px radius)
 */
const ServiceMarkTile: React.FC<EarlyAccessServiceMarkProps> = ({ integrationType, isLarge }) => {
  const large = isLarge === true;
  const index = catalogDistributorServices.indexOf(integrationType);
  if (index === -1) {
    return <CatalogServiceIcon integrationType={integrationType} isLarge={large} />;
  }

  const logo = getDistributorLogo(integrationType);
  const src = (distributorLogoUrls as Record<string, string | undefined>)[integrationType];
  if (logo !== undefined && src !== undefined && src !== '') {
    const size = distributorLogoSize(logo, large);
    return (
      <span
        aria-hidden="true"
        className={clsx(
          styles.logoTile,
          large && styles.logoTileLarge,
          logo.shape === 'wordmark' && styles.logoTileWide,
          logo.isFullBleed && styles.logoTileFullBleed
        )}
        style={{ backgroundColor: logo.plate }}
        data-testid={`distributor-logo-${integrationType}`}
      >
        <img src={src} alt="" width={size.width} height={size.height} className={styles.logoImage} />
      </span>
    );
  }

  return (
    <span
      aria-hidden="true"
      className={clsx(styles.letterTile, large && styles.letterTileLarge, tileClasses[index % 5])}
    >
      {distributorInitials[integrationType]}
    </span>
  );
};

/**
 * On a card the mark sits in the middle of a fixed box (`MARK_FRAME_WIDTH` x
 * 32 px), so a square logo and a wide word logo take the same width and the
 * names after them line up. The modal head shows one mark, so it has no box.
 */
const EarlyAccessServiceMarkComponent: React.FC<EarlyAccessServiceMarkProps> = ({ integrationType, isLarge }) => {
  if (isLarge === true) {
    return <ServiceMarkTile integrationType={integrationType} isLarge />;
  }
  return (
    <span className={styles.markFrame} data-testid={`service-mark-frame-${integrationType}`}>
      <ServiceMarkTile integrationType={integrationType} />
    </span>
  );
};

export const EarlyAccessServiceMark = TypedMemo(EarlyAccessServiceMarkComponent);
